import * as ewe from "@eyeo/webext-ad-filtering-solution";
import browser from "webextension-polyfill";
import { EXPERIMENT_FLAG, GET_DIALOG_MESSAGE_TYPE, GET_DIALOG_SHOULD_STILL_SHOW_MESSAGE_TYPE, DIALOG_ACTION_MESSAGE_TYPE, DISMISS_DIALOG_MESSAGE_TYPE, OPT_OUT_MESSAGE_TYPE, FEEDBACK_MESSAGE_TYPE, FEEDBACK_URL, STEP_PREF_KEY, TRIAL_START_DATE_PREF_KEY, MS_PER_DAY, OPD_KILL_SWITCH, } from "../shared/constants.js";
import { resolveNextTouchPoint, getTouchPoint, LAST_TOUCH_POINT_STEP, } from "./touchpoints.js";
const serverLogger = new ewe.telemetry.ServerLogger("reverse_trial");
async function toDataUrl(url) {
    const response = await fetch(url);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return `data:${blob.type};base64,${btoa(binary)}`;
}
async function ensureTrialIsNotInProgress(params) {
    var _a;
    const trialStartDate = (_a = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _a !== void 0 ? _a : 0;
    if (trialStartDate) {
        await advanceTrialStep(params, LAST_TOUCH_POINT_STEP);
    }
}
async function advanceTrialStep(params, newStep) {
    var _a;
    const currentStep = (_a = params.prefs.get(STEP_PREF_KEY)) !== null && _a !== void 0 ? _a : 0;
    if (currentStep < newStep) {
        await params.prefs.set(STEP_PREF_KEY, newStep);
    }
}
async function requestDialogHandler(message, params) {
    var _a, _b, _c, _d;
    const opdKillSwitch = Boolean(await ewe.experiments.getFlag(OPD_KILL_SWITCH));
    if ((await params.hasPremium()) || opdKillSwitch) {
        await ensureTrialIsNotInProgress(params);
        return null;
    }
    const lastShown = (_a = params.prefs.get(STEP_PREF_KEY)) !== null && _a !== void 0 ? _a : 0;
    let trialStartDate = (_b = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _b !== void 0 ? _b : 0;
    const flagEnabled = Boolean(await ewe.experiments.getFlag(EXPERIMENT_FLAG));
    if (!flagEnabled && !trialStartDate) {
        return null;
    }
    if (message.ytPremium && !trialStartDate) {
        return null;
    }
    if (!trialStartDate) {
        trialStartDate = Date.now();
        await params.prefs.set(TRIAL_START_DATE_PREF_KEY, trialStartDate);
    }
    const elapsedDays = Math.floor((Date.now() - trialStartDate) / MS_PER_DAY);
    const config = await resolveNextTouchPoint(lastShown, elapsedDays);
    if (!config) {
        return null;
    }
    if (message.ytPremium) {
        if (config.step === LAST_TOUCH_POINT_STEP) {
            await ensureTrialIsNotInProgress(params);
        }
        serverLogger.debug("dialog_skipped_yt_premium", {
            touchPointStep: config.step,
            installDate: (_c = params.prefs.get("installDate")) !== null && _c !== void 0 ? _c : 0,
            reverseTrialStartDate: trialStartDate,
        });
        return null;
    }
    if (config.showOnce) {
        await advanceTrialStep(params, config.step);
    }
    const logoUrl = await toDataUrl(params.logoUrl);
    serverLogger.info("dialog_shown", {
        touchPointStep: config.step,
        installDate: (_d = params.prefs.get("installDate")) !== null && _d !== void 0 ? _d : 0,
        reverseTrialStartDate: trialStartDate,
    });
    const urlParams = new URLSearchParams();
    urlParams.set("trialStarted", String(trialStartDate));
    const isOptedOut = await params.isOptedOut();
    if (!isOptedOut) {
        urlParams.set("s", "reverse_trial_opd");
        urlParams.set("opdStep", String(config.step));
        urlParams.set("rcid", await ewe.telemetry.getRotatingClientId());
        urlParams.set("av", params.addonVersion);
        urlParams.set("er", String(await ewe.remoteConfig.getRevisionId()));
        urlParams.set("ev", await ewe.experiments.getEncodedExperiments());
        urlParams.set("ca", await ewe.cohorts.getEncodedCohorts());
    }
    const premiumUrl = `${params.premiumUrl}?${urlParams.toString()}`;
    let target = "";
    if (config.hasTarget) {
        target = premiumUrl;
    }
    let learnMore;
    if (config.learnMore) {
        learnMore = {
            text: browser.i18n.getMessage(config.learnMore),
            url: premiumUrl,
        };
    }
    const dismiss = config.showDismissLink
        ? { text: browser.i18n.getMessage("reverse_trial_midpoint_decide_later") }
        : undefined;
    const optOut = config.optOutKey
        ? { text: browser.i18n.getMessage(config.optOutKey) }
        : undefined;
    let feedback;
    if (config.showFeedback) {
        feedback = {
            text: browser.i18n.getMessage("reverse_trial_submit_feedback"),
            url: FEEDBACK_URL + `&entry.1742752836=${config.step}`,
        };
        if (!isOptedOut) {
            feedback.url += `&entry.400370027=${params.addonVersion}`;
        }
    }
    return {
        title: browser.i18n.getMessage(config.titleKey),
        body: config.bodyKeys.map((entry) => ({
            text: browser.i18n.getMessage(entry.key),
            style: entry.style,
        })),
        button: config.buttonKey
            ? browser.i18n.getMessage(config.buttonKey)
            : undefined,
        target,
        logoUrl,
        touchPointStep: config.step,
        learnMore,
        dismiss,
        optOut,
        showOnce: config.showOnce,
        autoDismissMs: config.autoDismissMs,
        feedback,
    };
}
async function dialogActionHandler(message, params) {
    var _a, _b;
    await advanceTrialStep(params, message.touchPointStep);
    serverLogger.info("dialog_action", {
        action: message.action,
        touchPointStep: message.touchPointStep,
        installDate: (_a = params.prefs.get("installDate")) !== null && _a !== void 0 ? _a : 0,
        reverseTrialStartDate: (_b = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _b !== void 0 ? _b : 0,
    });
    if (!message.silent) {
        await dismissOpenDialogs();
    }
}
async function dialogShouldStillShowHandler(message, params) {
    var _a, _b, _c;
    const touchpoint = await getTouchPoint(message.touchPointStep);
    const currentStep = (_a = params.prefs.get(STEP_PREF_KEY)) !== null && _a !== void 0 ? _a : 0;
    let shouldStillShow;
    if (touchpoint === null || touchpoint === void 0 ? void 0 : touchpoint.showOnce) {
        shouldStillShow = currentStep === message.touchPointStep;
    }
    else {
        shouldStillShow = currentStep < message.touchPointStep;
    }
    serverLogger.debug("dialog_should_still_show_checked", {
        touchPointStep: message.touchPointStep,
        shouldStillShow,
        installDate: (_b = params.prefs.get("installDate")) !== null && _b !== void 0 ? _b : 0,
        reverseTrialStartDate: (_c = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _c !== void 0 ? _c : 0,
    });
    return shouldStillShow;
}
async function optOutHandler(message, params) {
    var _a, _b;
    await advanceTrialStep(params, LAST_TOUCH_POINT_STEP);
    serverLogger.info("opt_out", {
        touchPointStep: message.touchPointStep,
        installDate: (_a = params.prefs.get("installDate")) !== null && _a !== void 0 ? _a : 0,
        reverseTrialStartDate: (_b = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _b !== void 0 ? _b : 0,
    });
    await dismissOpenDialogs();
}
async function feedbackHandler(message, params) {
    var _a, _b;
    serverLogger.info("feedback_clicked", {
        touchPointStep: message.touchPointStep,
        installDate: (_a = params.prefs.get("installDate")) !== null && _a !== void 0 ? _a : 0,
        reverseTrialStartDate: (_b = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _b !== void 0 ? _b : 0,
    });
}
async function dismissOpenDialogs() {
    const tabs = await browser.tabs.query({});
    await Promise.all(tabs.map(async (tab) => {
        if (tab.id !== undefined) {
            try {
                await browser.tabs.sendMessage(tab.id, {
                    type: DISMISS_DIALOG_MESSAGE_TYPE,
                });
            }
            catch (_e) {
            }
        }
    }));
}
export function start(params) {
    params.port.on(GET_DIALOG_MESSAGE_TYPE, async (message) => await requestDialogHandler(message !== null && message !== void 0 ? message : {}, params));
    params.port.on(DIALOG_ACTION_MESSAGE_TYPE, async (message) => {
        await dialogActionHandler(message, params);
    });
    params.port.on(GET_DIALOG_SHOULD_STILL_SHOW_MESSAGE_TYPE, async (message) => await dialogShouldStillShowHandler(message, params));
    params.port.on(OPT_OUT_MESSAGE_TYPE, async (message) => {
        await optOutHandler(message, params);
    });
    params.port.on(FEEDBACK_MESSAGE_TYPE, async (message) => {
        await feedbackHandler(message, params);
    });
    const trustedTypes = [
        GET_DIALOG_MESSAGE_TYPE,
        GET_DIALOG_SHOULD_STILL_SHOW_MESSAGE_TYPE,
        DIALOG_ACTION_MESSAGE_TYPE,
        OPT_OUT_MESSAGE_TYPE,
        FEEDBACK_MESSAGE_TYPE,
    ];
    params.addTrustedMessageTypes("https://youtube.com", trustedTypes);
    params.addTrustedMessageTypes("https://www.youtube.com", trustedTypes);
    void params.hasPremium().then(async (premium) => {
        if (premium) {
            await ensureTrialIsNotInProgress(params);
        }
    });
}
